<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: "What cross-ledger movement is waiting for my decision?"
  Answer:   the pending one-sided transfers, one compact row each.
  Context:  the ledger that sent each item, by name - never a person's name.
  Controls: Accept (with the standard account picker) and Reject per item, each preceded
            by its own consequence sentence.
-->
<script setup lang="ts">
import { PageHeader, PageShell } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSession } from '@/app/session'
import {
  canViewTransfers,
  InboxList,
  RecentlyAcceptedList,
  type TransferView,
  useInbox,
} from '@/features/transfers'

const { t } = useI18n()
const session = useSession()
const inbox = useInbox()

const canView = computed(() => canViewTransfers(session.can))

/** A pending transfer's other side may be a ledger this principal is not a member of, so its name is unknown. */
function ledgerName(ledgerId: string): string {
  return (
    session.ledgers.value.find((ledger) => ledger.id === ledgerId)?.name ?? t('relationships.unknownLedger')
  )
}

async function onAccept(transfer: TransferView, targetAccountId: string): Promise<void> {
  await inbox.accept(transfer, targetAccountId)
}

async function onReject(transfer: TransferView): Promise<void> {
  await inbox.reject(transfer)
}

async function onReverse(transfer: TransferView): Promise<void> {
  await inbox.reverse(transfer)
}
</script>

<template>
  <PageShell class="gap-6">
    <PageHeader :title="t('transfers.inboxTitle')">
      <template #subtitle>{{ t('transfers.inboxSubtitle') }}</template>
    </PageHeader>

    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>
    <p v-else-if="!canView" class="text-[color:var(--color-text-muted)]">{{ t('transfers.noAccess') }}</p>

    <template v-else>
      <InboxList
        :items="inbox.items.value"
        :is-loading="inbox.isLoading.value"
        :error="inbox.error.value"
        :ledger-name="ledgerName"
        :is-busy="inbox.isBusy"
        @accept="onAccept"
        @reject="onReject"
        :retrying="inbox.retrying.value"
        @retry="inbox.retry()"
      />

      <RecentlyAcceptedList :items="inbox.justAccepted.value" :is-busy="inbox.isBusy" @reverse="onReverse" />
    </template>
  </PageShell>
</template>
